import { createBrowserRouter, redirect } from 'react-router-dom'

import { GenericErrorPage, NotFoundPage, SignOut } from '@/common/components'
import { ProtectedRoute } from '@/common/components/ProtectedRoute'
import UserRequiredContainer from '@/common/components/UserRequiredContainer/UserRequiredContainer'
import { Paths } from '@/common/utils'
import lazyWithRetry from '@/common/utils/dynamicImportWithRetry'
import { webStoreRedirectLoader } from '@/common/utils/webStoreRedirect'
import { Register } from '@/features/account/views'
import { LEGACY_PROFORMA_PATHNAME, redirectLegacyProforma } from '@/features/documents/redirectLegacyProforma'
import { zapierAuthorizeLoader } from '@/features/integrations/zapier/authorizeRequestStore'
import Login from '@/features/login/views/Login'
import Quote from '@/features/quotes/views/Quote'
import QuotesDashboard from '@/features/quotes/views/QuotesDashboard'
import UserWelcome from '@/features/user-welcome/UserWelcome'
import SplitProviderWrapper from '@/layout/SplitProviderWrapper'

import ChunkLoadError from './common/components/ChunkLoadError/ChunkLoadError'

// Low-risk routes - Convert first
const Settings = lazyWithRetry(() => import('@/features/settings/views/Settings'), 'Settings')
const AccountSettings = lazyWithRetry(
    () => import('@/features/settings/components/AccountSettings/AccountSettings'),
    'AccountSettings'
)
const OrganisationSettings = lazyWithRetry(
    () => import('@/features/settings/components/OrganisationSettings/OrganisationSettings'),
    'OrganisationSettings'
)
const ShippingSettings = lazyWithRetry(
    () => import('@/features/settings/components/ShippingSettings/ShippingSettings'),
    'ShippingSettings'
)
const StoreSettings = lazyWithRetry(
    () => import('@/features/settings/components/StoreSettings/StoreSettings'),
    'StoreSettings'
)
const Administration = lazyWithRetry(() => import('@/features/administration/views/Administration'), 'Administration')
const Migration = lazyWithRetry(() => import('@/features/administration/views/Migration'), 'Migration')
const DuplicateUserRemediation = lazyWithRetry(
    () => import('@/features/administration/views/DuplicateUserRemediation'),
    'DuplicateUserRemediation'
)

// Medium-risk routes - Convert second
const Materials = lazyWithRetry(() => import('@/features/materials/views/Materials'), 'Materials')
const Process = lazyWithRetry(() => import('@/features/processes/views/Process'), 'Process')
const RateTables = lazyWithRetry(() => import('@/features/rate-tables/views/RateTables'), 'RateTables')
const CustomerCentral = lazyWithRetry(
    () => import('@/features/customer-central/views/CustomerCentral'),
    'CustomerCentral'
)
const Documents = lazyWithRetry(() => import('@/features/documents/views/Documents'), 'Documents')
const ZapierAuthorize = lazyWithRetry(() => import('@/features/integrations/zapier/ZapierAuthorize'), 'ZapierAuthorize')

// High-risk routes - Convert last
const PaymentSuccess = lazyWithRetry(() => import('@/features/payments/PaymentSuccess'), 'PaymentSuccess')
const NoActiveSubscription = lazyWithRetry(
    () => import('@/features/settings/components/SubscriptionSettings/views/NoActiveSubscription'),
    'NoActiveSubscription'
)

// Loader function for redirecting to pricing request
const pricingRequestLoader = () => redirect('/settings/account#subscription')

const router = createBrowserRouter([
    {
        element: <SplitProviderWrapper />,
        children: [
            {
                path: Paths.SIGNOUT_PATHNAME,
                element: <SignOut />,
            },
            {
                path: Paths.ERROR_PATHNAME,
                element: <GenericErrorPage />,
            },
            {
                path: Paths.REGISTER_PATHNAME,
                element: <Register />,
            },
            {
                path: Paths.LOGIN_PATHNAME,
                element: <Login />,
            },
            {
                path: LEGACY_PROFORMA_PATHNAME,
                loader: redirectLegacyProforma,
            },
            {
                path: Paths.SHARED_V2_PATHNAME,
                element: (
                    <ChunkLoadError>
                        <Documents isPublicDocument={true} />
                    </ChunkLoadError>
                ),
            },
            {
                path: Paths.NOT_FOUND,
                element: <NotFoundPage />,
            },
            {
                path: Paths.WEB_STORE,
                loader: webStoreRedirectLoader,
            },
            {
                path: `${Paths.PAYMENTS_PATHNAME}/:quoteId/${Paths.PAYMENTS_SUCCESS_PATHNAME}`,
                element: (
                    <ChunkLoadError>
                        <PaymentSuccess />
                    </ChunkLoadError>
                ),
            },
            // Protected routes
            {
                path: '/',
                element: <UserRequiredContainer />,
                errorElement: <GenericErrorPage />,
                children: [
                    {
                        path: Paths.WELCOME_PATHNAME,
                        element: <UserWelcome />,
                    },
                    {
                        path: '/activate',
                        children: [
                            {
                                index: true,
                                loader: pricingRequestLoader,
                            },
                            {
                                path: 'billing-details',
                                loader: pricingRequestLoader,
                            },
                            {
                                path: 'payment-details',
                                loader: pricingRequestLoader,
                            },
                        ],
                    },
                    {
                        path: '/subscription',
                        loader: () => redirect('/settings/account#subscription'),
                    },
                    {
                        path: '/no-subscription',
                        element: (
                            <ChunkLoadError>
                                <NoActiveSubscription />
                            </ChunkLoadError>
                        ),
                    },
                    {
                        path: '/process-configuration',
                        element: (
                            <ChunkLoadError>
                                <Process />
                            </ChunkLoadError>
                        ),
                    },
                    {
                        path: Paths.RATE_TABLES,
                        element: (
                            <ChunkLoadError>
                                <RateTables />
                            </ChunkLoadError>
                        ),
                    },
                    {
                        path: Paths.CUSTOMER_CENTRAL_PATHNAME,
                        element: (
                            <ChunkLoadError>
                                <CustomerCentral />
                            </ChunkLoadError>
                        ),
                    },
                    {
                        path: Paths.MATERIALS_MANAGEMENT,
                        element: (
                            <ChunkLoadError>
                                <Materials />
                            </ChunkLoadError>
                        ),
                    },
                    {
                        path: Paths.QUOTE_PATHNAME,
                        element: <Quote />,
                    },
                    {
                        path: '/projects/:quoteId',
                        loader: ({ params }) => redirect(`/quotes/${params.quoteId}`),
                    },
                    {
                        path: Paths.DASHBOARD_PATHNAME,
                        element: <QuotesDashboard />,
                    },
                    {
                        path: Paths.ZAPIER_AUTHORIZE_PATHNAME,
                        // Stashes the request before this route's tree renders — the
                        // page sits behind UserRequiredContainer, which returns only a loading
                        // screen on a no-session hit, so nothing else can write the stash in
                        // time for a login round trip to read it back.
                        loader: zapierAuthorizeLoader,
                        element: (
                            <ChunkLoadError>
                                <ZapierAuthorize />
                            </ChunkLoadError>
                        ),
                    },
                    {
                        path: '/projects',
                        loader: () => redirect(Paths.DASHBOARD_PATHNAME),
                    },
                    {
                        path: Paths.DOCUMENT_PATHNAME,
                        element: (
                            <ChunkLoadError>
                                <Documents />
                            </ChunkLoadError>
                        ),
                    },
                    {
                        path: '/projects/:quoteId/documents/:documentType/:action?',
                        loader: ({ params }) => {
                            const { action, documentType, quoteId } = params
                            return redirect(`/quotes/${quoteId}/documents/${documentType}/${action ? action : ''}`)
                        },
                    },
                    {
                        path: '/settings',
                        element: (
                            <ChunkLoadError>
                                <Settings />
                            </ChunkLoadError>
                        ),
                        children: [
                            {
                                index: true,
                                loader: () => redirect('/settings/account'),
                            },
                            {
                                path: 'account',
                                element: (
                                    <ChunkLoadError>
                                        <AccountSettings />
                                    </ChunkLoadError>
                                ),
                            },
                            {
                                path: 'organisation',
                                element: (
                                    <ChunkLoadError>
                                        <OrganisationSettings />
                                    </ChunkLoadError>
                                ),
                            },
                            {
                                path: 'shipping',
                                element: (
                                    <ChunkLoadError>
                                        <ShippingSettings />
                                    </ChunkLoadError>
                                ),
                            },
                            {
                                path: 'store',
                                element: (
                                    <ChunkLoadError>
                                        <StoreSettings />
                                    </ChunkLoadError>
                                ),
                            },
                            {
                                path: 'subscription',
                                loader: () => redirect('/settings/account#subscription'),
                            },
                        ],
                    },
                    {
                        path: 'administration',
                        element: (
                            <ProtectedRoute
                                fallbackPath="/dashboard"
                                has={{ permissions: ['admin:site', 'support:site'] }}
                            >
                                <ChunkLoadError>
                                    <Administration />
                                </ChunkLoadError>
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'administration/migration',
                        element: (
                            <ProtectedRoute
                                fallbackPath="/administration"
                                has={{ permissions: ['admin:site'] }}
                            >
                                <ChunkLoadError>
                                    <Migration />
                                </ChunkLoadError>
                            </ProtectedRoute>
                        ),
                    },
                    {
                        path: 'administration/duplicate-users',
                        element: (
                            <ProtectedRoute
                                fallbackPath="/administration"
                                has={{ permissions: ['admin:site'] }}
                            >
                                <ChunkLoadError>
                                    <DuplicateUserRemediation />
                                </ChunkLoadError>
                            </ProtectedRoute>
                        ),
                    },
                ],
            },
            {
                path: '*',
                element: <NotFoundPage />,
            },
        ],
    },
])

export default router
